import { User } from 'firebase/auth';

import ContentService from './contentService';
import EnrollmentService from './enrollmentService';
import SectionService from './sectionService';
import { UserProfileService } from './userProfileService';
import ContentApi from '../api/contentApi';
import SectionApi from '../api/sectionApi';
import SkillApi from '../api/skillApi';
import StatApi, { NewStatData } from '../api/statApi';
import Completion from '../types/completion';
import { ContentData } from '../types/contentTypes';
import SectionData from '../types/SectionData';
import SkillData from '../types/skillData';
import { BaseStatData, StatType } from '../types/statData';
import { getOrLogError } from '../utils/functions';

export class StatService {
  private static async areContentsCompletedForUser(contents: ContentData[], userUid: string) {
    const contentIds = contents.map(content => content.id);
    const contentStats = await StatApi.getStatsWithTargetIdsAndUserId(contentIds, userUid);
    const completedContentIds = contentStats.map(contentStat => contentStat.targetId);
    return contentIds.every(contentId => completedContentIds.includes(contentId));
  }

  private static async areSectionsCompletedForUser(sections: SectionData[], userUid: string) {
    const sectionsIds = sections.map(section => section.id);
    const sectionStats = await StatApi.getStatsWithTargetIdsAndUserId(sectionsIds, userUid);
    const completedSectionIds = sectionStats.map(skillPageStat => skillPageStat.targetId);
    return sectionsIds.every(skillPageId => completedSectionIds.includes(skillPageId));
  }

  /**
   * Adds a section stat in case a similar one does not exist. A section stat is similar if it has the same combination
   * of userUid and sectionId. That would mean that the current user has already completed the section thus we do not
   * need to add a new section stat
   * @return true if stat was added, false if it already exists.
   */
  private static async addTargetStatForUserIfNotExists(stat: NewStatData) {
    const alreadyExists = await this.existsStatForUser(stat.targetId, stat.userUid);
    if (alreadyExists) return false;
    await StatApi.addStat(stat);
    return true;
  }

  public static async getTargetStatForUser(
    targetId: string,
    userUid: string | undefined
  ): Promise<BaseStatData | undefined> {
    if (userUid === undefined) return undefined;
    return await StatApi.getStatWithTargetIdAndUserId(targetId, userUid);
  }

  public static async existsStatForUser(targetId: string, userUid: string): Promise<boolean> {
    return !!(await StatApi.getStatWithTargetIdAndUserId(targetId, userUid));
  }
  // public static async existStatsForUser(targetIds: string[], userUid: string): Promise<boolean[]> {
  //   const statExistPromises = targetIds.map(id => {
  //     return this.existsStatForUser(id, userUid);
  //   });
  //   return await Promise.all(statExistPromises);
  // }
  /**
   * Marks the given content as completed for the user by adding a content stat. This in turn may mark a section as
   * completed if all contents for the section are completed. The pattern is repeated for the skill as well.
   * @param completedContent Section to mark as completed.
   * @param user User which has completed the section.
   */
  public static async markContentAsCompleted(completedContent: ContentData, user: User) {
    const ContentStat: NewStatData = {
      targetId: completedContent.id,
      skillId: completedContent.skillId,
      userUid: user.uid,
      type: StatType.Content,
    };

    await this.addTargetStatForUserIfNotExists(ContentStat);
    // Check if the other section stats in the same skill page have been completed
    const siblingContents = (await ContentApi.getContentsBySectionId(completedContent.sectionId)).filter(
      section => section.id !== completedContent.id
    );

    const areAllContentsCompleted = await this.areContentsCompletedForUser(siblingContents, user.uid);
    if (!areAllContentsCompleted) {
      return { sectionComplete: false, skillComplete: false };
    }

    const section = await SectionApi.getSectionById(completedContent.sectionId);
    const complete = await this.markSectionAsCompleted(section, user);
    return { ...complete };
  }

  /**
   * Marks the given skill page as completed for the user by adding a skill page stat.
   * @param completedSection Skill page to mark as completed.
   * @param user User which has completed the skill page.
   */
  public static async markSectionAsCompleted(completedSection: SectionData, user: User) {
    const sectionStat: NewStatData = {
      targetId: completedSection.id,
      skillId: completedSection.skillId,
      userUid: user.uid,
      type: StatType.Section,
    };
    await this.addTargetStatForUserIfNotExists(sectionStat);
    // Check if the other sections in the same skill have been completed
    const siblingSections = (await SectionApi.getSectionsBySkillId(completedSection.skillId)).filter(
      section => section.id !== completedSection.id
    );

    const areAllSectionsCompleted = await this.areSectionsCompletedForUser(siblingSections, user.uid);
    if (!areAllSectionsCompleted) {
      return { sectionComplete: true, skillComplete: false };
    }
    const skill = await SkillApi.getSkillById(completedSection.skillId);
    const complete = await this.markSkillAsCompleted(skill, user);
    return { ...complete, sectionComplete: true };
  }

  /**
   * Marks the given skill as completed for the user by adding a skill stat.
   * @param completedSkill Skill to mark as completed.
   * @param user User which has completed the skill.
   */
  public static async markSkillAsCompleted(completedSkill: SkillData, user: User) {
    const skillStat: NewStatData = {
      targetId: completedSkill.id,
      userUid: user.uid,
      skillId: completedSkill.id,
      type: StatType.Skill,
    };
    await this.addTargetStatForUserIfNotExists(skillStat);
    return { skillComplete: true };
  }

  public static async getSectionCompletion(section: SectionData, user: User): Promise<Completion> {
    const contents = await ContentApi.getContentsBySectionId(section.id);
    if (contents.length === 0) return { completed: 0, total: 0 };

    const incompleteContents = await ContentService.getIncompleteContentsForUser(section, user);
    const incompleteContentsIds = incompleteContents.map(content => content.id);
    const completeContents = contents.filter(content => !incompleteContentsIds.includes(content.id));
    return { completed: completeContents.length, total: contents.length };
  }

  public static async getSkillCompletion(skill: SkillData, user: User): Promise<Completion> {
    const sections = await SectionApi.getSectionsBySkillId(skill.id);
    if (sections.length === 0) return { completed: 0, total: 0 };

    const incompleteSections = await SectionService.getIncompleteSectionForUser(skill, user);
    const incompleteSectionIds = incompleteSections.map(content => content.id);
    const completeSections = sections.filter(content => !incompleteSectionIds.includes(content.id));
    return { completed: completeSections.length, total: sections.length };
  }

  public static async getSkillCompletionPercentage(skill: SkillData, user: User) {
    const skillCompletion = await this.getSkillCompletion(skill, user);
    if (skillCompletion.total === 0) return 0;
    return Math.floor((skillCompletion.completed / skillCompletion.total) * 100);
  }

  public static async getCompletedSkillIds(uuid: string): Promise<string[]> {
    const completedSkillStats = await StatApi.getStatsWithTypeAndUserId(StatType.Skill, uuid);
    return completedSkillStats.map(skill => skill.skillId);
  }

  public static async getCompletedSectionsInSkillForUser(skill: SkillData, uuid: string) {}

  public static async getSectionStatSummaryForUsersInSkill(skill: SkillData) {
    const userProfileMap = await UserProfileService.getUserProfilesAsMap();
    const stats = await StatApi.getSectionStatsBySkillId(skill.id);
    const userIds = await EnrollmentService.getUserIdsEnrolledInSkill(skill);
    return userIds.map(userId => {
      const userProfile = getOrLogError(userProfileMap.get(userId));
      const completedCount = stats.filter(stat => stat.userUid === userId).length;
      return {
        userProfile,
        completedCount,
      };
    });
  }

  // public static async getSkillPageCompletionChain(skillPage: OldSkillPageData, user: User) {
  //   const skillPageComplete = await this.existsStatForUser(skillPage.id, user.uid);
  //   const skill = await ContentApi.getSkillById(skillPage.skillId);
  //   const skillComplete = await this.existsStatForUser(skillPage.skillId, user.uid);
  //   const moduleComplete = await this.existsStatForUser(skill.moduleId, user.uid);
  //   return {
  //     skillPageComplete,
  //     skillComplete,
  //     moduleComplete,
  //   };
  // }
}
